// src/data/streamPolicies.js

// --- 1. REGIONAL CADENCE POLICY ---
export const REGION_POLICY = {
  India: {
    cadenceDays: 10,
    tone: "Direct & ROI-Focused",
    buyerQuestion: "How fast can this deploy?",
    waitStep: "10 Days",
  },
  SEA: {
    cadenceDays: 18,
    tone: "Relationship & Peer-Led",
    buyerQuestion: "Who else in my market uses this?",
    waitStep: "18 Days",
  },
  Default: {
    cadenceDays: 14,
    tone: "Balanced",
    buyerQuestion: "Why change now?",
    waitStep: "14 Days",
  },
};

// --- 2. VERTICAL CONTENT POLICY ---
export const VERTICAL_POLICY = {
  "Financial Services": {
    theme: "Risk & Compliance",
    asset1: "The Cost of Skills Gaps in Regulated Markets",
    asset2: "Case Study: Modernizing the Bank Workforce",
  },
  Technology: {
    theme: "Speed & Innovation",
    asset1: "Accelerating Tech Deployment via Skills",
    asset2: "Case Study: IT Services Efficiency",
  },
  Default: {
    theme: "Workforce Transformation",
    asset1: "2025 Global Skills Report",
    asset2: "Productivity Unlocked Guide",
  },
};

// --- 3. MESSAGE LIBRARY (The Advisory Guidance) ---
export const MESSAGE_LIBRARY = {
  context_email: {
    channel: "Email",
    type: "Context Setter",
    intent_goal: "Loosen Status Quo",
    cta_type: "Soft (Read)",
    ops_score: 5,
    marketer: {
      objective: "Reframe problem in buyer terms. No product pitch.",
      subjectLines: [
        "Workforce readiness is now a business risk (not an HR initiative)",
        "The talent gap is hitting productivity first — here’s what we’re seeing",
      ],
      previewText:
        "What’s changing in your region — and what leaders are doing first.",
      bodyBlocks: [
        "Macro shift (regional lens)",
        "Cost of inaction (productivity / attrition / time-to-competency)",
        "What leading teams prioritize first (no vendor mention)",
      ],
    },
    mops: {
      program: "APAC_Nurture_Awareness",
      smartList: "Entry: Segment Match AND Not in Opp/Active Cycle",
      tokensNeeded: ["region", "vertical", "persona", "tier", "stage"],
      waitRule: "Use region cadenceDays",
      scoring: "Engagement (3pts) ≠ Intent",
      suppress: ["unsubscribed", "opportunity_open"],
      route: "Do not route to SDR on touch 1",
    },
  },
  proof_email: {
    channel: "Email",
    type: "Peer Proof",
    intent_goal: "Validate Solution",
    cta_type: "Medium (View)",
    ops_score: 8,
    marketer: {
      objective: "Show, don't tell. Validate that peers are already moving.",
      subjectLines: [
        "How [Vertical] leaders are closing the skills gap",
        "Case Study: 30% faster onboarding in [Region]",
      ],
      previewText: "See the playbook used by top performing teams.",
      bodyBlocks: [
        "Peer validation story",
        "Metrics that matter (Time to proficiency)",
        "Link to full case study",
      ],
    },
    mops: {
      scoring: "Click (8pts) = Engaged",
      route: "No alert yet",
    },
  },
  choice_logic: {
    channel: "Interactive",
    type: "Choice Logic",
    intent_goal: "Self-Qualify",
    cta_type: "Soft (Choose)",
    ops_score: 12,
    marketer: {
      objective: "Get the buyer to declare their specific priority.",
      subjectLines: ["What is your top workforce priority for Q1?"],
      bodyBlocks: ["Option A: Speed", "Option B: Risk", "Option C: Scale"],
    },
    mops: {
      scoring: "Choice = 12pts + Segmentation Update",
      route: "Map choice to next stream content",
    },
  },
  business_case_builder: {
    channel: "Email",
    type: "Business Case Builder",
    intent_goal: "Internal Buy-In",
    cta_type: "Medium (Use)",
    ops_score: 18,
    marketer: {
      objective: "Arm the champion to sell internally.",
      subjectLines: ["The ROI of skills modernization"],
      bodyBlocks: ["Cost of inaction calculator", "Executive summary slide"],
    },
    mops: {
      scoring: "Download = 18pts (High Intent)",
      route: "Alert SDR if score > 60",
    },
  },
  buying_group_expansion: {
    channel: "Email",
    type: "Buying Group Expansion",
    intent_goal: "Multi-Stakeholder",
    cta_type: "Soft (Forward)",
    ops_score: 10,
    marketer: {
      objective: "Encourage sharing with IT/Finance/HR counterparts.",
      subjectLines: ["Share this with your technical lead"],
      bodyBlocks: ["Technical implementation brief", "Security overview"],
    },
    mops: {
      scoring: "Forward/New Lead = 10pts",
      route: "Trigger Buying Group Alert",
    },
  },
  human_task: {
    channel: "Task",
    type: "SDR Outreach",
    intent_goal: "Activate Human Touch",
    cta_type: "Hard (Meeting)",
    ops_score: 0,
    condition: "High Intent Signals Only",
    marketer: {
      objective: "Human verification of digital signals.",
      script: "I noticed your team is researching [Topic]...",
    },
    mops: {
      trigger: "Score > 60 OR Hand-raiser",
      sla: "24 Hours",
    },
  },
};

// --- 4. STREAM BLUEPRINTS (The Sequence) ---
export const STREAM_BLUEPRINTS = {
  Awareness: [
    { template: "context_email", dayOffsetMultiplier: 0 },
    { template: "proof_email", dayOffsetMultiplier: 1 },
    { template: "choice_logic", dayOffsetMultiplier: 1.5 }, // Faster follow-up
  ],
  Engagement: [
    { template: "proof_email", dayOffsetMultiplier: 0 },
    { template: "business_case_builder", dayOffsetMultiplier: 1 },
    { template: "human_task", dayOffsetMultiplier: 2, conditional: true },
  ],
  Enablement: [
    { template: "business_case_builder", dayOffsetMultiplier: 0 },
    { template: "buying_group_expansion", dayOffsetMultiplier: 1 },
    { template: "human_task", dayOffsetMultiplier: 1, conditional: true },
  ],
};

// --- 5. DEFAULT SEGMENTS (The "Seed" Data) ---
export const DEFAULT_SEGMENTS = [
  // India
  {
    region: "India",
    vertical: "Technology",
    persona: "L&D Leader",
    stage: "Awareness",
    tier: "Enterprise",
  },
  {
    region: "India",
    vertical: "Technology",
    persona: "HR Leader",
    stage: "Awareness",
    tier: "Enterprise",
  },
  {
    region: "India",
    vertical: "Financial Services",
    persona: "L&D Leader",
    stage: "Awareness",
    tier: "Enterprise",
  },
  // SEA
  {
    region: "SEA",
    vertical: "Technology",
    persona: "L&D Leader",
    stage: "Awareness",
    tier: "Enterprise",
  },
  {
    region: "SEA",
    vertical: "Financial Services",
    persona: "HR Leader",
    stage: "Awareness",
    tier: "Enterprise",
  },
];
